/** Core entities */
import { baseEntitySchema } from '../entity.js'
import {
  audioFeatureSchema,
  audioSchema,
  isAudio,
  isAudioChordsFeature,
  isAudioFeature,
  isAudioFeatureMap,
  isAudioSeparation,
} from './audio.js'
import type { Audio, AudioChordsFeature, AudioFeature, AudioFeatureUrls, AudioMedia } from './audio.js'
import { chatSchema, isChat } from './chat.js'
import type { Chat } from './chat.js'
import { commentKinds, commentSchema, isChatComment, isComment, isNudgeComment, isProposalComment } from './comment.js'
import type { Comment, CommentKind } from './comment.js'
import { entityLinkSchema, isEntityLink } from './entitylink.js'
import type { EntityLink } from './entitylink.js'
import { linkSchema, isLink } from './link.js'
import type { Link } from './link.js'
import { folderSchema, isFolder } from './folder.js'
import type { Folder } from './folder.js'
import { imageSchema, isImage } from './image.js'
import type { Image, ImageElement } from './image.js'
import { isNote, noteSchema } from './note.js'
import type { Note } from './note.js'
import { isPage, pageSchema } from './page.js'
import type { Page } from './page.js'
import { isPdf, pdfSchema } from './pdf.js'
import type { PDF } from './pdf.js'
import { isProfile, profileSchema, publicProfileSchema } from './profile.js'
import type { Profile } from './profile.js'
import { isTable, tableSchema } from './table.js'
import type { Table } from './table.js'
import { isTodo, todoSchema } from './todo.js'
import type { Todo } from './todo.js'
import { isVideo, videoSchema } from './video.js'
import type { Video, VideoMedia } from './video.js'
import type { EntityRef } from './ref.js'
import refSchema, { isEntityRef, isEntityRefStrict } from './ref.js'
import { blockRefSchema } from './block-ref.js'
import type { BlockRef } from './block-ref.js'
import { type SearchFolder, searchFolderSchema, isSearchFolder } from './searchfolder.js'
import { type RuleFiringBatchEntity, ruleFiringBatchEntitySchema, isRuleFiringBatch } from './rule-firing-batch.js'

/** Exports */

export {
  audioFeatureSchema,
  audioSchema,
  chatSchema,
  commentKinds,
  commentSchema,
  folderSchema,
  imageSchema,
  isAudio,
  isAudioChordsFeature,
  isAudioFeature,
  isAudioFeatureMap,
  isChatComment,
  isLink,
  isAudioSeparation,
  isSearchFolder,
  isChat,
  isComment,
  isNudgeComment,
  isProposalComment,
  isEntityLink,
  isFolder,
  isImage,
  isNote,
  isPage,
  isPdf,
  isProfile,
  isTable,
  isTodo,
  isRuleFiringBatch,
  isVideo,
  isEntityRef,
  isEntityRefStrict,
  noteSchema,
  pageSchema,
  pdfSchema,
  profileSchema,
  publicProfileSchema,
  tableSchema,
  todoSchema,
  ruleFiringBatchEntitySchema,
  videoSchema,
  refSchema,
  blockRefSchema,
  type Audio,
  type AudioChordsFeature,
  type AudioFeature,
  type AudioFeatureUrls,
  type AudioMedia,
  type Chat,
  type Comment,
  type CommentKind,
  type EntityLink,
  type Folder,
  type Image,
  type ImageElement,
  type Note,
  type Page,
  type PDF,
  type Profile,
  type Link,
  type Table,
  type Todo,
  type RuleFiringBatchEntity,
  type Video,
  type VideoMedia,
  type EntityRef,
  type BlockRef,
  type SearchFolder,
}

const schemas = {
  folder: folderSchema,
  audio: audioSchema,
  page: pageSchema,
  profile: profileSchema,
  image: imageSchema,
  entitylink: entityLinkSchema,
  chat: chatSchema,
  todo: todoSchema,
  comment: commentSchema,
  video: videoSchema,
  pdf: pdfSchema,
  note: noteSchema,
  table: tableSchema,
  link: linkSchema,
  'rule-firing-batch': ruleFiringBatchEntitySchema,
  tr: baseEntitySchema,
  th: baseEntitySchema,
  td: baseEntitySchema,
  thead: baseEntitySchema,
  tbody: baseEntitySchema,
  ref: refSchema,
  searchfolder: searchFolderSchema,
}

/** Add new core entity types here */
export const coreEntityNames = Object.keys(schemas) as unknown as readonly (keyof typeof schemas)[]
export type CoreEntityName = (typeof coreEntityNames)[number]

export default schemas
